import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  FormField,
  Input,
  LoadingState,
} from '@sakhtar/design-system/react';
import {
  getMessengerConfigStatus,
  testMessengerNotification,
  type MessengerNotificationConfigStatus,
  type MessengerNotificationResult,
} from '../../lib/operatorApi.js';
import { OperatorPage } from './OperatorShell.js';

export function MessengerNotificationsPage() {
  const [status, setStatus] = useState<MessengerNotificationConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [testText, setTestText] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<MessengerNotificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getMessengerConfigStatus()
      .then((res) => {
        setStatus(res);
        setLoading(false);
      })
      .catch(() => {
        setError('دریافت وضعیت اتصال بات‌ها با خطا مواجه شد.');
        setLoading(false);
      });
  }, []);

  async function handleSendTest(e: FormEvent, type: 'signup' | 'estimate' = 'signup') {
    if (e) e.preventDefault();
    setTestBusy(true);
    setTestResult(null);
    setError(null);
    try {
      const res = await testMessengerNotification(testText.trim() || undefined, type);
      setTestResult(res);
    } catch {
      setError('ارسال پیام تست با خطا مواجه شد.');
    } finally {
      setTestBusy(false);
    }
  }

  if (loading) {
    return (
      <OperatorPage title="اعلان‌های ثبت‌نام پیام‌رسان">
        <LoadingState />
      </OperatorPage>
    );
  }

  return (
    <OperatorPage title="اعلان‌های پیام‌رسان و استعلام‌ها (تلگرام و بله)">
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {/* وضعیت سرویس‌ها */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Card title="پیام‌رسان بله (Bale Bot)">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>وضعیت اتصال:</span>
            {status?.baleConfigured ? (
              <Badge tone="success">متصل و فعال</Badge>
            ) : (
              <Badge tone="danger">پیکربندی‌نشده</Badge>
            )}
          </div>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 13,
              color: 'var(--sk-color-text-secondary, #6b7280)',
            }}
          >
            اعلان‌های ثبت‌نام آنلاین به کانال یا گروه پشتیبانی بله ارسال می‌شود.
          </p>
        </Card>

        <Card title="پیام‌رسان تلگرام (Telegram Bot)">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span>وضعیت اتصال:</span>
            {status?.telegramConfigured ? (
              <Badge tone="success">
                {status.telegramMode === 'relay' ? 'متصل (رله تلگرام)' : 'متصل (مستقیم Bot API)'}
              </Badge>
            ) : (
              <Badge tone="danger">پیکربندی‌نشده</Badge>
            )}
          </div>

          <div
            style={{
              fontSize: 12,
              lineHeight: 1.8,
              color: 'var(--sk-color-text-secondary, #4b5563)',
            }}
          >
            <div>
              <strong>حالت ارتباط:</strong>{' '}
              {status?.telegramMode === 'relay' ? 'سرور رله داخلی (Relay)' : 'مستقیم'}
            </div>
            {status?.targetChatId ? (
              <div>
                <strong>شناسه مقصد:</strong> <code dir="ltr">{status.targetChatId}</code>
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              <span>ثبت‌نام: {status?.notifySignup ? '✅ فعال' : '❌ غیرفعال'}</span>
              <span>استعلام هزینه: {status?.notifyEstimate ? '✅ فعال' : '❌ غیرفعال'}</span>
            </div>
          </div>
        </Card>

        <Card title="وب‌هوک سفارشی (Custom Webhook)">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>وضعیت وب‌هوک:</span>
            {status?.webhookConfigured ? (
              <Badge tone="success">فعال</Badge>
            ) : (
              <Badge tone="default">غیرفعال (اختیاری)</Badge>
            )}
          </div>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 13,
              color: 'var(--sk-color-text-secondary, #6b7280)',
            }}
          >
            ارسال JSON رویدادهای ثبت‌نام به اندپوینت‌های اختصاصی و CRM.
          </p>
        </Card>
      </div>

      {/* آزمایش ارسال نوتیفیکیشن */}
      <Card title="آزمایش و راستی‌آزمایی ارسال اعلان (Test Send)">
        {testResult ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {testResult.telegram ? (
              <Alert
                tone={testResult.telegram.success ? 'success' : 'danger'}
                title={`ارسال به تلگرام: ${testResult.telegram.success ? 'موفقیت‌آمیز' : 'ناموفق'}`}
              >
                {testResult.telegram.error
                  ? `علت خطا: ${testResult.telegram.error}`
                  : 'پیام با موفقیت در کانال / گروه تلگرام تحویل داده شد.'}
              </Alert>
            ) : null}

            {testResult.bale ? (
              <Alert
                tone={testResult.bale.success ? 'success' : 'danger'}
                title={`ارسال به بله: ${testResult.bale.success ? 'موفقیت‌آمیز' : 'ناموفق'}`}
              >
                {testResult.bale.error
                  ? `علت خطا: ${testResult.bale.error}`
                  : 'پیام با موفقیت در بله تحویل داده شد.'}
              </Alert>
            ) : null}

            {testResult.webhook ? (
              <Alert
                tone={testResult.webhook.success ? 'success' : 'warning'}
                title={`وب‌هوک: ${testResult.webhook.success ? 'موفق' : 'ارسال نشد'}`}
              >
                {testResult.webhook.error
                  ? testResult.webhook.error
                  : 'وب‌هوک با موفقیت فراخوانی شد.'}
              </Alert>
            ) : null}
          </div>
        ) : null}

        <form
          onSubmit={(e) => handleSendTest(e, 'signup')}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <FormField
            label="متن دلخواه پیام آزمایشی (اختیاری)"
            hint="در صورت خالی بودن، متن استاندارد پیش‌فرض سیستم همراه با زمان شمسی ارسال خواهد شد."
          >
            <Input
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="مثال: تست اتصال ربات تلگرام و بله سامانه ابری ساختار..."
            />
          </FormField>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button type="submit" variant="primary" disabled={testBusy}>
              {testBusy ? 'در حال ارسال پیام تست...' : 'ارسال پیام آزمایشی به بات‌ها 🚀'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={testBusy}
              onClick={(e) => void handleSendTest(e, 'estimate')}
            >
              ارسال نمونه اعلان استعلام قیمت (Estimate) 📊
            </Button>
          </div>
        </form>
      </Card>

      {/* راهنمای تنظیم متغیرهای محیطی */}
      <Card title="راهنمای تنظیم متغیرهای محیطی (.env)">
        <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.8 }}>
          برای فعال‌سازی اعلان‌های خودکار ثبت‌نام و استعلام قیمت، متغیرهای زیر را در فایل{' '}
          <code dir="ltr">.env</code> سرور وارد نمایید:
        </p>

        <pre
          dir="ltr"
          style={{
            background: 'var(--sk-surface-inset, #1f2937)',
            color: '#f9fafb',
            padding: 16,
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.6,
            overflowX: 'auto',
          }}
        >
          {`# اتصال به تلگرام از طریق رله داخلی (سازگار با سرورهای ایران و دیتاسنترها)
TELEGRAM_MODE=relay
TELEGRAM_ENABLED=true
TELEGRAM_RELAY_URL=https://telegram-relay.sakhtar.net/api/telegram-relay
TELEGRAM_RELAY_KEY=your-relay-secret-key
TELEGRAM_DEFAULT_CHAT_ID=-100xxxxxxxxxx
TELEGRAM_CHAT_ID=-100xxxxxxxxxx
TELEGRAM_TIMEOUT_MS=12000
TELEGRAM_NOTIFY_SIGNUP=true
TELEGRAM_NOTIFY_ESTIMATE=true

# پیام‌رسان بله (Bale Bot API)
BALE_BOT_TOKEN="123456789:ABCdefGhIjkLmNoPqRsTuVwXyZ"
BALE_CHAT_ID="987654321"

# وب‌هوک دلخواه (اختیاری)
SIGNUP_NOTIFICATION_WEBHOOK_URL="https://example.com/api/webhooks/signup"`}
        </pre>

        <h4 style={{ margin: '16px 0 8px', fontSize: 14 }}>
          اطلاعات ارسال‌شده در هر پیام ثبت‌نام و استعلام:
        </h4>
        <ul
          style={{
            margin: 0,
            paddingRight: 20,
            fontSize: 13,
            lineHeight: 1.8,
            color: 'var(--sk-color-text-secondary, #4b5563)',
          }}
        >
          <li>نام سازمان و نام تجاری ثبت‌شده</li>
          <li>
            نامک اختصاصی و آدرس پنل سازمان (<code dir="ltr">/t/:slug</code>)
          </li>
          <li>نام و نام خانوادگی مدیر و ثبت‌نام‌کننده</li>
          <li>شماره موبایل مدیر (شناسه ورود اصلی)</li>
          <li>نوع طرح انتخابی و تعداد صندلی‌های درخواستی</li>
          <li>برآورد هزینه ماهانه، کل فاکتور، و تخفیف اعمال‌شده</li>
          <li>زمان دقیق ثبت به همراه تاریخ و ساعت شمسی به وقت تهران</li>
        </ul>
      </Card>
    </OperatorPage>
  );
}
